import { Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { ComponentType, JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { EGoalType, Goal } from '@feathr/blackbox';
import { Button, ButtonValid, Drawer, InlineEdit, Well } from '@feathr/components';
import ConversionPixelTriggerGoal from '@feathr/extender/components/Goal/ConversionPixelTriggerGoal';
import DonationFormSubmissionGoal from '@feathr/extender/components/Goal/DonationFormSubmissionGoal';
import FormSubmissionGoal from '@feathr/extender/components/Goal/FormSubmissionGoal';
import GoalTypeSelect from '@feathr/extender/components/Goal/GoalLayout/GoalTypeSelect';
import GroupEntryGoal from '@feathr/extender/components/Goal/GroupEntryGoal';
import URLVisitGoal from '@feathr/extender/components/Goal/URLVisitGoal';
import { cssVar } from '@feathr/hooks';

import { useGoalDrawerForm } from './GoalDrawer.hooks';

import * as styles from './GoalDrawer.css';

export interface IGoalDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const typeComponentMap: Record<EGoalType, ComponentType<{ goal: Goal }>> = {
  conversion_pixel: ConversionPixelTriggerGoal,
  donation: DonationFormSubmissionGoal,
  form_submission: FormSubmissionGoal,
  group_entry: GroupEntryGoal,
  page_view: URLVisitGoal,
};

function GoalDrawer({ isOpen, onClose }: Readonly<IGoalDrawerProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const { goal, handleSave, isSaving, isValid, nameErrors, buttonErrors, reset } =
    useGoalDrawerForm(onClose, isOpen);

  function handleClose(): void {
    reset();
    onClose();
  }

  const convType = goal?.get('conv_type');
  const TypeSpecificForm =
    convType !== undefined && convType in typeComponentMap
      ? typeComponentMap[convType as EGoalType]
      : null;

  return (
    <Drawer
      description={t("Define a goal once — it'll track across every campaign and flight.")}
      isOpen={isOpen}
      name={'create-goal-drawer'}
      onClose={handleClose}
      title={t('Create goal')}
      width={cssVar('--drawer-width-144')}
    >
      <Drawer.Content addVerticalGap={true}>
        {goal && (
          <Well borderless={true}>
            <Stack gap={theme.spacing['4']} w={'100%'}>
              <InlineEdit
                attribute={'name'}
                className={styles.title}
                model={goal}
                validationError={nameErrors[0]}
              />
              <GoalTypeSelect goal={goal} isAdvancedConversionTracking={false} />
              {TypeSpecificForm && <TypeSpecificForm goal={goal} />}
            </Stack>
          </Well>
        )}
      </Drawer.Content>
      <Drawer.Actions>
        <Button name={'cancel'} onClick={handleClose}>
          {t('Cancel')}
        </Button>
        <ButtonValid
          disabled={!isValid}
          errors={buttonErrors}
          isLoading={isSaving}
          name={'create-goal'}
          onClick={handleSave}
          type={'primary'}
        >
          {t('Create goal')}
        </ButtonValid>
      </Drawer.Actions>
    </Drawer>
  );
}

export default observer(GoalDrawer);
